import { Options, RenderNode, documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { createElement, useMemo } from 'react';

import { ContentfulRichText } from 'src/data/Contentful/ContentfulTypes';
import { Link } from 'src/general/components/Link/Link';
import { HeadingElementName, HeadingLevel, scaleDownHeadings } from 'src/general/helpers/scaleDownHeadings';

type HeadingBlocks = BLOCKS.HEADING_1 | BLOCKS.HEADING_2 | BLOCKS.HEADING_3 | BLOCKS.HEADING_4 | BLOCKS.HEADING_5;

const lookup: Record<HeadingBlocks, Exclude<HeadingElementName, 'h6'>> = {
  [BLOCKS.HEADING_1]: 'h1',
  [BLOCKS.HEADING_2]: 'h2',
  [BLOCKS.HEADING_3]: 'h3',
  [BLOCKS.HEADING_4]: 'h4',
  [BLOCKS.HEADING_5]: 'h5',
};

/**
 * Contentful rendering options.
 * All links are mapped to using internal Link component.
 * Headings can be scaled down to ensure document structure makes sense.
 */
const getOptions = (headingsScale: HeadingLevel = 1): Options => {
  const renderNode: RenderNode = {
    [INLINES.HYPERLINK]: (node, children) => (
      <Link href={node.data.uri}>{Array.isArray(children) ? children[0] : children}</Link>
    ),
  };

  const scale = scaleDownHeadings(headingsScale);

  if (scale) {
    Object.entries(lookup).forEach(([block, elementName]) => {
      renderNode[block] = (_node, children) => createElement(scale[elementName] as string, null, children);
    });
  }

  return { renderNode };
};

interface RichTextContentfulProps {
  className?: string;
  headingsScale?: HeadingLevel;
  children?: ContentfulRichText;
}

/**
 * Render rich text from Contentful.
 */
export const RichTextContentful = ({ className, headingsScale, children }: RichTextContentfulProps) => (
  <div className={className}>
    {useMemo(() => (children ? documentToReactComponents(children.json, getOptions(headingsScale)) : null), [children])}
  </div>
);
